import { useState, type FormEvent } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth-context';
import { PROJECTS, type Project } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: (id: string) => void;
}

export default function CreateChecklistModal({ open, onClose, onCreated }: Props) {
  const { user, profile } = useAuth();
  const [title, setTitle] = useState('');
  const [project, setProject] = useState<Project>('PP');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  function reset() {
    setTitle('');
    setProject('PP');
    setDescription('');
    setError(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user || !profile) return;
    setError(null);
    setSubmitting(true);
    try {
      const ref = await addDoc(collection(db, 'checklists'), {
        title: title.trim(),
        project,
        description: description.trim() || null,
        status: 'open',
        createdBy: user.uid,
        createdByName: profile.displayName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        itemCount: 0,
        passCount: 0,
        failCount: 0,
        pendingCount: 0
      });
      reset();
      onClose();
      onCreated?.(ref.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create checklist');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__head">
          <h2 className="modal__title">New checklist</h2>
          <button className="modal__close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <form className="modal__form" onSubmit={handleSubmit}>
          <label className="field">
            <span className="field__label">Title</span>
            <input
              type="text"
              required
              placeholder="e.g. PP Ops — May 21 polish batch"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
          </label>

          <label className="field">
            <span className="field__label">Project</span>
            <select value={project} onChange={(e) => setProject(e.target.value as Project)}>
              {PROJECTS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Description (optional)</span>
            <textarea
              rows={3}
              placeholder="What's this checklist covering? Recent shipping, scope, context…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>

          {error && <div className="form-error">{error}</div>}

          <div className="modal__actions">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={submitting || !title.trim()}>
              {submitting ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
